import React from "react";
import { useNavigate } from "react-router-dom";
import { Paperclip, ChevronRight } from "lucide-react";
import Avatar from "../common/Avatar";
import FifaVerifiedBadge, { isFifaVerified } from "../common/FifaVerifiedBadge";
import SearchRequestStatusChip from "./SearchRequestStatusChip";
import RequestAttribution from "./RequestAttribution";
import { usePermission } from "../../hooks/usePermission";
import {
  formatRequestTitle,
  formatSecondary,
  formatRequestMeta,
  formatTerms,
  formatTermsShort,
  formatSentDate,
  formatSearchingSince,
  isWinnerRecipient,
  isPendingOnly,
} from "../../utils/searchRequestFormat";

// One club search request — the A1 per-request card (Commissioned Search, Annex
// A1) built on the Redesign 2.0 N3-4/M4 card. The card body is unchanged; the
// recipient strip below it became the per-agent STATUS CHAIN:
//   • winner row — chips Accepted → Searching (since [date] · no expiry) →
//     Delivered · N / Nothing found, plus View proposals / Chat / Read reason;
//   • loser rows — "Closed, [winner] was first" (row_resolution);
//   • declined rows — the quoted short reason;
//   • pending/seen rows — Pending / Seen chip;
//   • a request-level resolution line ("[winner] accepted first, he has the job").
// The card danger action is Close (A4) once someone accepted, or Withdraw (A14b)
// while still pending. Mobile (M·A1): every agent's chain STACKS on its own rows;
// chips wrap, nothing shrinks or truncates (TRANSLATIONS §3).

// A text action link inside a recipient row (View proposals / Chat / Read reason).
const RowAction = ({ label, onClick }) => (
  <button
    type="button"
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    className="text-[11.5px] font-bold text-[#2283FF] whitespace-nowrap hover:underline"
  >
    {label}
  </button>
);

// The chip progression for one recipient. Winner rows show the chain; the other
// kinds show a single terminal chip (+ quote / resolution text handled by the row).
const ChainChips = ({ recipient }) => {
  const { status, delivered_count: delivered, searching_since: since } = recipient;
  if (isWinnerRecipient(recipient)) {
    const showSearching = ["searching", "delivered", "nothing_found"].includes(status);
    return (
      <span className="inline-flex items-center gap-1 flex-wrap">
        <SearchRequestStatusChip status="accepted" />
        {showSearching && (
          <>
            <ChevronRight className="w-3 h-3 text-gray-300 flex-none" aria-hidden="true" />
            <SearchRequestStatusChip status="searching" />
          </>
        )}
        {status === "delivered" && (
          <>
            <ChevronRight className="w-3 h-3 text-gray-300 flex-none" aria-hidden="true" />
            <SearchRequestStatusChip status="delivered" count={delivered} />
          </>
        )}
        {status === "nothing_found" && (
          <>
            <ChevronRight className="w-3 h-3 text-gray-300 flex-none" aria-hidden="true" />
            <SearchRequestStatusChip status="nothing_found" />
          </>
        )}
        {showSearching && since && status === "searching" && (
          <span className="text-[11px] text-[#9CA3AF] whitespace-nowrap">
            {formatSearchingSince(since)}
          </span>
        )}
      </span>
    );
  }
  // Pending/seen/declined/expired/closed → single chip (Pending for a raw sent).
  const chip = status === "sent" ? "pending" : status;
  return <SearchRequestStatusChip status={chip} />;
};

// One recipient's full chain row — avatar + name (+FIFA), the chip chain, and the
// contextual actions. Stacks on mobile (M·A1) via flex-col; the chain wraps.
const ChainRow = ({ recipient, onViewProposals, onChat, onReadReason, onViewLive }) => {
  const { status, decline_reason: reason, row_resolution: resolution, agent } = recipient;
  const winner = isWinnerRecipient(recipient);
  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3 py-2 border-t border-[#F3F4F6] first:border-t-0">
      <span className="flex items-center gap-2 min-w-0 sm:w-[190px] sm:flex-none">
        <Avatar src={agent?.avatar} fallbackText={agent?.display_name} size={26} />
        <span className="flex items-center gap-1.5 min-w-0">
          <span className="text-[12.5px] font-semibold text-[#111827] truncate">
            {agent?.display_name}
          </span>
          {isFifaVerified(agent) && <FifaVerifiedBadge size="sm" />}
        </span>
      </span>
      <span className="flex flex-col gap-1 min-w-0 flex-1">
        <span className="flex items-center gap-2 flex-wrap">
          <ChainChips recipient={recipient} />
          {winner && status === "delivered" && (
            <RowAction label="View proposals" onClick={() => onViewProposals(recipient)} />
          )}
          {winner && status === "nothing_found" && (
            <RowAction label="Read reason" onClick={() => onReadReason(recipient)} />
          )}
          {/* A7 — open the read-only live commissioned request ("Searching ↗"). */}
          {status === "searching" && (
            <RowAction label="Searching ↗" onClick={onViewLive} />
          )}
          {winner && (
            <RowAction label="Chat" onClick={() => onChat(recipient)} />
          )}
        </span>
        {status === "closed" && resolution && (
          <span className="text-[11.5px] text-[#9CA3AF]">{resolution}</span>
        )}
        {status === "declined" && reason && (
          <span className="text-[11.5px] italic text-gray-400" title={reason}>
            “{reason}”
          </span>
        )}
      </span>
    </div>
  );
};

const SearchRequestCard = ({ request, onClose, onWithdraw }) => {
  const navigate = useNavigate();
  const canClose = usePermission("delete_tenders");
  const secondary = formatSecondary(request);
  const meta = formatRequestMeta(request);
  const terms = formatTerms(request);
  const termsShort = formatTermsShort(request);
  const sent = formatSentDate(request);
  const isClosed = request.status === "closed";
  const pendingOnly = isPendingOnly(request);
  // A15: an archived request is READ-ONLY — no Close/Withdraw danger action.
  // Closed rows offer "Send again" (a new invitation round); the other archive
  // row types (delivered / nothing found / all declined) get their contextual
  // actions from the chain itself (View proposals / Read reason).
  const isArchived = request.lifecycle_bucket === "archive";
  const recipients = request.recipients || [];

  const openDetail = () => navigate(`/network/trusted-agent/${request.id}`);
  const openDelivery = () => navigate(`/network/trusted-agent/${request.id}/delivery`);
  const openLive = () => navigate(`/network/trusted-agent/${request.id}/live`);
  const sendAgain = () => navigate("/network/trusted-agent/new");
  const openChat = (recipient) => {
    if (recipient?.chat_channel_id) navigate(`/inbox?channel=${recipient.chat_channel_id}`);
    else navigate("/inbox");
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={openDetail}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openDetail();
        }
      }}
      className="bg-white border border-[#E5E7EB] rounded-[14px] md:rounded-xl p-[15px] md:px-5 md:py-[18px] flex flex-col gap-2.5 md:gap-3 shadow-[0_1px_2px_rgba(17,24,39,0.05)] text-left cursor-pointer transition-shadow hover:shadow-md hover:border-[#D6DBE3] focus:outline-none focus:ring-2 focus:ring-[#2283FF]/30"
    >
      <div className="flex items-start gap-3.5">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <span className="text-[14.5px] md:text-[15px] font-bold text-[#111827]">
            {formatRequestTitle(request)}
            {secondary && (
              <span className="font-normal text-gray-500 text-[12px]">
                {" · "}
                {`also ${secondary}`}
              </span>
            )}
            {isClosed && (
              <span className="ml-2 text-[11px] font-semibold text-gray-400 uppercase">
                Closed
              </span>
            )}
          </span>
          {meta && (
            <span className="text-[12px] md:text-[12.5px] text-[#6B7280]">{meta}</span>
          )}
          {terms && (
            <span className="text-[11.5px] md:text-[12px] text-[#4B5563] bg-[#F3F4F6] px-2.5 py-1 rounded-lg self-start">
              <span className="md:hidden">Terms: {termsShort}</span>
              <span className="hidden md:inline">Terms: {terms}</span>
            </span>
          )}
          {request.has_document && (
            <span className="inline-flex items-center gap-1 text-[11.5px] text-[#4B5563] self-start">
              <Paperclip className="w-3 h-3 text-[#2283FF]" /> Document attached
            </span>
          )}
        </div>
        {/* Right actions (desktop): View + the danger verb. Withdraw (A14b)
            while still pending, Close (A4) once an agent accepted. */}
        <div className="hidden md:flex gap-2 flex-none">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openDetail();
            }}
            className="bg-white text-[#111827] border border-[#E5E7EB] rounded-lg text-[12.5px] font-semibold px-[13px] py-2 hover:bg-[#F3F4F6]"
          >
            View
          </button>
          {/* Archived closed request → Send again (new invitation round). */}
          {isArchived && isClosed && canClose && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                sendAgain();
              }}
              className="bg-white text-[#2283FF] border border-[#BFDBFE] rounded-lg text-[12.5px] font-semibold px-[13px] py-2 hover:bg-[#EFF6FF]"
            >
              Send again
            </button>
          )}
          {/* Live request danger action — Withdraw while pending, Close once an
              agent accepted. Never on an archived (read-only) request. */}
          {!isArchived && !isClosed && canClose && pendingOnly && onWithdraw && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onWithdraw(request);
              }}
              className="bg-white text-[#DC2626] border border-[#FECACA] rounded-lg text-[12.5px] font-semibold px-[13px] py-2 hover:bg-[#FEE2E2]"
            >
              Withdraw
            </button>
          )}
          {!isArchived && !isClosed && canClose && !pendingOnly && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onClose(request);
              }}
              className="bg-white text-[#DC2626] border border-[#FECACA] rounded-lg text-[12.5px] font-semibold px-[13px] py-2 hover:bg-[#FEE2E2]"
            >
              Close request
            </button>
          )}
        </div>
      </div>

      {/* Resolution line — "[winner] accepted first, he has the job" (A1). */}
      {request.resolution_line && (
        <span className="text-[12px] font-semibold text-[#16A34A]">
          {request.resolution_line}
        </span>
      )}

      {/* Per-agent status chain (A1). */}
      <div className="flex flex-col border-t border-[#F3F4F6] pt-2.5">
        <span className="text-[11px] text-[#9CA3AF] mb-1">
          {sent ? `Sent ${sent} to:` : "Sent to:"}
        </span>
        {recipients.map((r) => (
          <ChainRow
            key={r.id}
            recipient={r}
            onViewProposals={openDelivery}
            onReadReason={openDelivery}
            onChat={openChat}
            onViewLive={openLive}
          />
        ))}
      </div>

      {/* A14 pending rule line. On mobile the danger verb (Close/Withdraw) lives
          in the detail view the card taps into — parity with the M4 board, which
          shows no card actions (chain stacking is M·A1's only mobile change). */}
      {pendingOnly && (
        <span className="text-[11px] text-[#9CA3AF] italic">
          Nothing runs in the network until an agent accepts.
        </span>
      )}
      <div className="md:hidden flex justify-end">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            openDetail();
          }}
          className="text-[12px] font-semibold text-[#2283FF]"
        >
          View request →
        </button>
      </div>

      {(request.created_by || (isClosed && request.closed_by)) && (
        <RequestAttribution
          compact
          createdBy={request.created_by}
          createdAt={request.created_at}
          closedBy={isClosed ? request.closed_by : null}
          closedAt={request.closed_at}
        />
      )}
    </div>
  );
};

export default SearchRequestCard;
